"use client";

import {
	Close as DialogClose,
	Content as DialogContent,
	Description as DialogDescription,
	Overlay as DialogOverlay,
	Portal as DialogPortal,
	Root as DialogRoot,
	Title as DialogTitle,
} from "@radix-ui/react-dialog";
import { Loader2, PencilLine, Sparkles, X } from "lucide-react";
import useChangelogStore from "~/stores/changelog";
import { ChangelogEditor } from "./changelog-editor";
import { ChangelogViewer } from "./changelog-viewer";
import { useChangelog } from "./use-changelog";
import { useBuildVersion } from "./use-version";

const DOT_GRID_STYLE = {
	backgroundImage:
		"radial-gradient(color-mix(in oklab, var(--primary) 22%, transparent) 1px, transparent 1.4px)",
	backgroundSize: "15px 15px",
} as const;

export const ChangelogModal = () => {
	const open = useChangelogStore((state) => state.open);
	const mode = useChangelogStore((state) => state.mode);
	const passkey = useChangelogStore((state) => state.passkey);
	const close = useChangelogStore((state) => state.close);

	const { data, isLoading, isError, error } = useChangelog(mode, passkey, open);
	const { data: build } = useBuildVersion();

	const isEdit = mode === "edit";
	const environment = build?.environment;

	return (
		<DialogRoot
			onOpenChange={(next) => {
				if (!next) {
					close();
				}
			}}
			open={open}
		>
			<DialogPortal>
				<DialogOverlay className="data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-background/70 backdrop-blur-sm data-[state=closed]:animate-out data-[state=open]:animate-in" />
				<DialogContent className="data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 fixed top-1/2 left-1/2 z-50 flex h-[min(88vh,680px)] w-[min(960px,95vw)] -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-2xl border border-border bg-background shadow-2xl duration-200 focus:outline-none focus-visible:outline-none data-[state=closed]:animate-out data-[state=open]:animate-in">
					{/* Decorative header band */}
					<div className="relative overflow-hidden border-border/60 border-b bg-gradient-to-br from-primary/[0.07] via-background to-background px-6 py-4">
						<div
							aria-hidden
							className="pointer-events-none absolute inset-0 opacity-50"
							style={DOT_GRID_STYLE}
						/>
						<div className="relative flex items-center justify-between gap-4">
							<div className="flex items-center gap-3">
								<div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary ring-1 ring-primary/20">
									{isEdit ? (
										<PencilLine className="h-5 w-5" />
									) : (
										<Sparkles className="h-5 w-5" />
									)}
								</div>
								<div>
									<DialogTitle className="font-bold text-foreground text-lg leading-tight tracking-tight">
										{isEdit ? "Changelog editor" : "What's new"}
									</DialogTitle>
									<DialogDescription className="text-[12px] text-muted-foreground">
										{isEdit
											? "Curate entries, toggle visibility, then save or export."
											: "Everything new in uTranscribe."}
									</DialogDescription>
								</div>
							</div>
							<div className="flex items-center gap-2">
								{isEdit && (
									<span className="hidden items-center gap-1 rounded-full bg-amber-500/10 px-2 py-1 font-semibold text-[10px] text-amber-600 uppercase tracking-wider ring-1 ring-amber-500/20 sm:inline-flex dark:text-amber-400">
										Editing
									</span>
								)}
								{build && (
									<span className="hidden items-center gap-1.5 rounded-full border border-border/70 bg-background/60 px-2.5 py-1 font-medium text-[11px] text-muted-foreground tabular-nums sm:inline-flex">
										v{build.version}
										{environment && environment !== "production" && (
											<span className="text-primary uppercase">
												{environment}
											</span>
										)}
									</span>
								)}
								<DialogClose
									aria-label="Close"
									className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
								>
									<X className="h-4 w-4" />
								</DialogClose>
							</div>
						</div>
					</div>

					{/* Body */}
					{(() => {
						if (isLoading) {
							return (
								<div className="flex flex-1 items-center justify-center text-muted-foreground">
									<Loader2 className="mr-2 h-5 w-5 animate-spin" />
									Loading changelog…
								</div>
							);
						}
						if (isError) {
							return (
								<div className="flex flex-1 items-center justify-center px-6 text-center text-destructive text-sm">
									{error instanceof Error
										? error.message
										: "Failed to load the changelog."}
								</div>
							);
						}
						if (!data) {
							return null;
						}
						return isEdit ? (
							<ChangelogEditor changelog={data} passkey={passkey} />
						) : (
							<ChangelogViewer changelog={data} />
						);
					})()}
				</DialogContent>
			</DialogPortal>
		</DialogRoot>
	);
};
